import { useMemo, useState } from "react";
import { format, addDays, addMinutes, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Clock, MapPin } from "lucide-react";
import { type PricingRule, type SpecialPriceDate, calculateBookingPrice } from "@/lib/pricing";
import { pickBestCourts, countAvailableCourts } from "@/lib/courtPacking";
import { useTranslation } from "react-i18next";
import WaitlistCta from "./WaitlistCta";

interface CourtInfo {
  id: string;
  name: string;
  sort_order?: number;
}

interface BookingInfo {
  id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
}

interface AutoBookingViewProps {
  courts: CourtInfo[];
  bookings: BookingInfo[];
  hallHours: { start: number; end: number };
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  bookingHorizon: number;
  timeStepMinutes: number;
  pricingRules?: PricingRule[];
  specialDates?: SpecialPriceDate[];
  hallId?: string;
  defaultDuration?: number;
  maxDuration?: number;
  categorySlug?: string;
  courtGroupId?: string;
  onBook: (courtIds: string[], startTime: Date, duration: number) => void;
}

const DURATION_OPTIONS = [60, 90, 120];

export default function AutoBookingView({
  courts,
  bookings,
  hallHours,
  selectedDate,
  onDateChange,
  bookingHorizon,
  timeStepMinutes,
  pricingRules = [],
  specialDates = [],
  hallId,
  defaultDuration = 60,
  maxDuration = 120,
  categorySlug,
  courtGroupId,
  onBook,
}: AutoBookingViewProps) {
  const { t } = useTranslation("booking");
  const [duration, setDuration] = useState(defaultDuration);
  const [courtCount, setCourtCount] = useState(1);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [assignedCourts, setAssignedCourts] = useState<string[]>([]);

  // Filter courts by group if specified
  const scopedCourts = useMemo(() => {
    if (!courtGroupId) return courts;
    return courts.filter((c) => (c as any).court_group_id === courtGroupId);
  }, [courts, courtGroupId]);

  const showCourtCount = scopedCourts.length >= 3;
  const activeDurations = DURATION_OPTIONS.filter((d) => d <= maxDuration);

  // Date navigation (round buttons)
  const dates = useMemo(() => {
    const arr: Date[] = [];
    const today = startOfDay(new Date());
    for (let i = 0; i < Math.min(bookingHorizon, 14); i++) {
      arr.push(addDays(today, i));
    }
    return arr;
  }, [bookingHorizon]);

  // Generate time slots
  const timeSlots = useMemo(() => {
    const slots: Array<{ time: string; start: Date; available: number; price: number | null }> = [];
    const day = startOfDay(selectedDate);
    const now = new Date();

    for (let h = hallHours.start; h < hallHours.end; h++) {
      for (let m = 0; m < 60; m += timeStepMinutes) {
        const slotStart = addMinutes(day, h * 60 + m);
        const slotEnd = addMinutes(slotStart, duration);

        // Skip past slots
        if (slotStart <= now) continue;
        // Skip if end exceeds hall hours
        if (slotEnd > addMinutes(day, hallHours.end * 60)) continue;

        const available = countAvailableCourts(scopedCourts, bookings, slotStart, slotEnd);
        const price = hallId && pricingRules.length > 0
          ? calculateBookingPrice(pricingRules, hallId, slotStart, duration, categorySlug ?? "standard", specialDates)
          : null;

        slots.push({
          time: format(slotStart, "HH:mm"),
          start: slotStart,
          available,
          price: price ? price.total * courtCount : null,
        });
      }
    }
    return slots;
  }, [selectedDate, hallHours, timeStepMinutes, duration, scopedCourts, bookings, hallId, pricingRules, specialDates, categorySlug, courtCount]);

  // Group slots by period
  const periods = useMemo(() => {
    const morning = timeSlots.filter((s) => parseInt(s.time) < 12);
    const afternoon = timeSlots.filter((s) => parseInt(s.time) >= 12 && parseInt(s.time) < 17);
    const evening = timeSlots.filter((s) => parseInt(s.time) >= 17);
    return [
      { label: "Förmiddag", slots: morning },
      { label: "Eftermiddag", slots: afternoon },
      { label: "Kväll", slots: evening },
    ].filter((p) => p.slots.length > 0);
  }, [timeSlots]);

  const handleTimeClick = (slot: typeof timeSlots[0]) => {
    if (slot.available < courtCount) return;
    const end = addMinutes(slot.start, duration);
    const courtIds = pickBestCourts(scopedCourts, bookings, slot.start, end, courtCount);
    if (courtIds.length < courtCount) return;
    setSelectedTime(slot.time);
    setAssignedCourts(courtIds);
  };

  const handleBook = () => {
    if (!selectedTime || assignedCourts.length === 0) return;
    const slot = timeSlots.find((s) => s.time === selectedTime);
    if (!slot) return;
    onBook(assignedCourts, slot.start, duration);
  };

  const assignedCourtNames = assignedCourts.map((id) => scopedCourts.find((c) => c.id === id)?.name ?? "Bana").join(", ");

  // If a time is selected, show confirmation
  if (selectedTime) {
    const slot = timeSlots.find((s) => s.time === selectedTime);
    return (
      <div className="space-y-4">
        <button
          onClick={() => { setSelectedTime(null); setAssignedCourts([]); }}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Ändra tid
        </button>

        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-5 space-y-3">
          <div className="space-y-1">
            <p className="text-lg font-semibold">
              {format(selectedDate, "EEEE d MMMM", { locale: sv })}
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              {selectedTime}–{format(addMinutes(slot!.start, duration), "HH:mm")} · {duration} min
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>Bana tilldelas automatiskt</span>
            </div>
          </div>

          {slot?.price != null && (
            <p className="text-2xl font-bold">{slot.price} kr</p>
          )}

          <Button className="w-full h-12 text-base" onClick={handleBook}>
            Boka{slot?.price != null ? ` · ${slot.price} kr` : ""}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Date selector */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
        {dates.map((d) => {
          const isSelected = format(d, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
          const isToday = format(d, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
          return (
            <button
              key={d.toISOString()}
              onClick={() => { onDateChange(d); setSelectedTime(null); }}
              className={cn(
                "flex flex-col items-center px-3 py-1.5 rounded-full text-xs shrink-0 transition-colors",
                isSelected ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent",
              )}
            >
              <span className="uppercase font-medium">
                {isToday ? "Idag" : format(d, "EEE", { locale: sv })}
              </span>
              <span>{format(d, "d/M")}</span>
            </button>
          );
        })}
      </div>

      {/* Duration pills */}
      <div className="flex gap-2">
        {activeDurations.map((d) => (
          <button
            key={d}
            onClick={() => { setDuration(d); setSelectedTime(null); }}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium transition-colors",
              duration === d ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent",
            )}
          >
            {d} min
          </button>
        ))}
      </div>

      {/* Court count pills */}
      {showCourtCount && (
        <div className="flex gap-2 items-center">
          <span className="text-xs text-muted-foreground">Antal banor:</span>
          {[1, 2, 3].filter((n) => n <= scopedCourts.length).map((n) => (
            <button
              key={n}
              onClick={() => { setCourtCount(n); setSelectedTime(null); }}
              className={cn(
                "w-9 h-9 rounded-full text-sm font-medium transition-colors",
                courtCount === n ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent",
              )}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {/* Time slots grouped by period */}
      {periods.map((period) => (
        <div key={period.label}>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{period.label}</p>
          <div className="space-y-1">
            {period.slots.map((slot) => {
              const isFree = slot.available >= courtCount;
              return (
                // 009/T3: en full lucka var en död disabled-rad. Wrappern låter
                // kö-CTA:n stå bredvid — en knapp kan inte ligga i en knapp.
                <div key={slot.time} className="flex items-center gap-2">
                <button
                  onClick={() => isFree && handleTimeClick(slot)}
                  disabled={!isFree}
                  className={cn(
                    "w-full flex items-center justify-between rounded-lg border px-4 py-3 transition-colors text-left",
                    isFree ? "hover:bg-accent cursor-pointer" : "opacity-50 cursor-not-allowed",
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-semibold tabular-nums">{slot.time}</span>
                    <Badge variant={isFree ? "default" : "secondary"} className="text-[10px]">
                      {isFree ? "Ledigt" : "Fullt"}
                    </Badge>
                  </div>
                  {slot.price != null && (
                    <span className="text-sm font-medium text-muted-foreground">{slot.price} kr</span>
                  )}
                </button>
                {!isFree && (
                  <WaitlistCta
                    compact
                    courts={scopedCourts}
                    date={selectedDate}
                    startTime={slot.time}
                    durationMinutes={duration}
                    className="shrink-0"
                  />
                )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {timeSlots.length === 0 && (
        // 009: strängen var hårdkodad svenska utanför i18n. Nycklarna finns i
        // sv+en; ingen ny hårdkodad sträng tillkommer.
        <p className="text-sm text-muted-foreground text-center py-8">{t("noAvailableTimes")}</p>
      )}
    </div>
  );
}
